// Cadence computation — pure logic. Single source of truth for the
// [21, 56]-day bounded formula. Same module runs in Cloud Functions
// (server-enforced) and on the client (for UI previews).
//
// Source: CogniPrint_Recalibration_Cadence_Research_v2.md §3.1,
// CogniPrint_Architecture_v1_6.docx §3.7.1.

import type {
  CadenceFactors,
  RecalibrationPreference,
  RecalibrationSchedule,
  RecalibrationStatus,
} from '../types/recalibration';
import { RECALIBRATION_BOUNDS } from '../types/recalibration';

export interface CadenceInputs {
  lastRecalibration: Date;
  profileCreatedAt: Date;
  activityVolumeHours: number;
  minDimensionConfidence: number;
  recentNegativeAdaptationCount: number;
  userPreference: RecalibrationPreference;
  now?: Date;
}

export interface CadenceResult {
  nextRecalibration: Date;
  factors: CadenceFactors;
  cycleDays: number;
}

function activityVolumeFactor(hours: number): number {
  if (hours >= 5) return 0.9;
  if (hours >= 2) return 1.0;
  return 1.2;
}

function profileAgeFactor(profileCreatedAt: Date, now: Date): number {
  const ageMs = now.getTime() - profileCreatedAt.getTime();
  const ageDays = ageMs / (24 * 60 * 60 * 1000);
  if (ageDays < 30) return 1.0;
  if (ageDays < 90) return 0.85;
  return 1.0;
}

function confidenceFactor(minConfidence: number): number {
  return minConfidence < 0.5 ? 0.9 : 1.0;
}

function recentFeedbackFactor(negativeCount: number): number {
  return negativeCount >= 2 ? 0.75 : 1.0;
}

function userPreferenceFactor(pref: RecalibrationPreference): number {
  switch (pref) {
    case 'more_often':
      return 0.75;
    case 'less_often':
      return 1.5;
    case 'default':
    default:
      return 1.0;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function computeCadenceFactors(
  inputs: CadenceInputs,
): CadenceFactors {
  const now = inputs.now ?? new Date();
  const activity = activityVolumeFactor(inputs.activityVolumeHours);
  const profileAge = profileAgeFactor(inputs.profileCreatedAt, now);
  const confidence = confidenceFactor(inputs.minDimensionConfidence);
  const recentFeedback = recentFeedbackFactor(
    inputs.recentNegativeAdaptationCount,
  );
  const userPref = userPreferenceFactor(inputs.userPreference);

  const rawComposite =
    activity * profileAge * confidence * recentFeedback * userPref;
  const composite = clamp(rawComposite, 0.75, 2.0);

  return {
    activityVolume: activity,
    profileAge,
    confidence,
    recentFeedback,
    userPreference: userPref,
    composite,
  };
}

/**
 * Compute the next recalibration date. Clamp to [21, 56] days regardless
 * of factor composition. This is the server-authoritative computation;
 * client calls exist only for UI preview.
 */
export function computeNextRecalibration(
  inputs: CadenceInputs,
): CadenceResult {
  const factors = computeCadenceFactors(inputs);
  const proposedDays = RECALIBRATION_BOUNDS.defaultDays * factors.composite;
  const cycleDays = clamp(
    Math.round(proposedDays),
    RECALIBRATION_BOUNDS.minDays,
    RECALIBRATION_BOUNDS.maxDays,
  );

  const nextRecalibration = new Date(inputs.lastRecalibration);
  nextRecalibration.setUTCDate(
    nextRecalibration.getUTCDate() + cycleDays,
  );

  return { nextRecalibration, factors, cycleDays };
}

/**
 * Server-side cooldown check. Enforce in Cloud Functions before any
 * recalibration mutation. 21-day hard floor from last completed
 * recalibration, regardless of scheduled date.
 */
export function isRecalibrationAllowed(
  lastRecalibration: Date | null,
  now: Date = new Date(),
  minDaysOverride?: number,
): { allowed: true } | { allowed: false; retryAt: Date; reason: string } {
  if (!lastRecalibration) return { allowed: true };

  const minDays = minDaysOverride ?? RECALIBRATION_BOUNDS.minDays;
  const msSinceLast = now.getTime() - lastRecalibration.getTime();
  const daysSinceLast = msSinceLast / (24 * 60 * 60 * 1000);

  if (daysSinceLast >= minDays) return { allowed: true };

  const retryAt = new Date(lastRecalibration);
  retryAt.setUTCDate(retryAt.getUTCDate() + minDays);
  return {
    allowed: false,
    retryAt,
    reason: `Recalibration cooldown: ${Math.ceil(minDays - daysSinceLast)} day(s) remaining.`,
  };
}

export function deriveRecalibrationStatus(
  schedule: Pick<RecalibrationSchedule, 'nextRecalibration'>,
  now: Date = new Date(),
): RecalibrationStatus {
  const next = new Date(schedule.nextRecalibration);
  const msDelta = next.getTime() - now.getTime();
  const daysUntil = msDelta / (24 * 60 * 60 * 1000);

  if (daysUntil > 7) return 'upcoming';
  if (daysUntil > 0) return 'due_soon';
  if (daysUntil > -RECALIBRATION_BOUNDS.softResetThresholdDays)
    return daysUntil >= -0.5 ? 'ready' : 'overdue';
  return 'reanchor';
}

export function daysUntilRecalibration(
  nextRecalibration: Date | string,
  now: Date = new Date(),
): number {
  const next =
    typeof nextRecalibration === 'string'
      ? new Date(nextRecalibration)
      : nextRecalibration;
  const msDelta = next.getTime() - now.getTime();
  return Math.round(msDelta / (24 * 60 * 60 * 1000));
}
